import { styled } from '@linaria/react'
import React, { FC, useMemo, useCallback } from 'react'
import { FormattedMessage } from 'react-intl'
import { colors } from '../styles/colors'
import { Typography } from '../ui/Typography'
import { ReferralShareButton } from './ReferralShareButton'
import CopyToClipboardIcon from '../svgr/CopyToCb'
import { ButtonRegular } from '../ui/Button'
import { generateReferralUrl } from '../utils/shareUtils'
import { useCopyToClipboard } from '../hooks/useCopyToClipboard'
import { useAppContext } from '../contexts/App.context'

export const ReferralLink: FC = () => {
  const { referralKey } = useAppContext()
  const { copyUrlToClipboard } = useCopyToClipboard()

  const referralData = useMemo(() => {
    if (!referralKey) {
      return {
        url: '',
        displayUrl: 'ref/...',
      }
    }

    const referralUrl = generateReferralUrl(referralKey)
    const displayUrl = `ref/${referralKey}`

    return {
      url: referralUrl,
      displayUrl,
    }
  }, [referralKey])

  const handleCopyLink = useCallback(async () => {
    if (!referralData.url) {
      return
    }

    await copyUrlToClipboard(referralData.url)
  }, [referralData.url, copyUrlToClipboard])

  if (!referralData.url) {
    return null
  }

  return (
    <Container>
      <Title>
        <Typography fontSize={13} fontWeight={700} color={colors.grey900}>
          <FormattedMessage
            id="bonus.yourReferralLink"
            defaultMessage="Ваша реферальная ссылка"
          />
        </Typography>
      </Title>

      <InputContainer>
        <LinkInput>
          <TruncatedText
            fontSize={14}
            fontWeight={400}
            color={colors.textSecondary}
          >
            {referralData.displayUrl}
          </TruncatedText>
        </LinkInput>

        <ButtonsContainer>
          <ActionButton
            color={colors.bgPrimary}
            onClick={handleCopyLink}
            title="Copy to clipboard"
            isWithTypographyWrapper={false}
          >
            <CopyToClipboardIcon />
          </ActionButton>

          <StyledShareButton url={referralData.url} />
        </ButtonsContainer>
      </InputContainer>
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: ${colors.primaryLight};
  padding: 16px;
  border-radius: 16px;
`

const Title = styled.div`
  margin-bottom: 4px;
`

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${colors.surfacePrimary};
  border: 1px solid ${colors.borderPrimary};
  border-radius: 12px;
  padding: 12px;
`

const LinkInput = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  min-width: 0; /* Allow text to truncate */
`

const TruncatedText = styled(Typography)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 80px;
`

const ButtonsContainer = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: 8px;
`

const ActionButton = styled(ButtonRegular)`
  padding: 4px;
  cursor: pointer;
  transition: transform 0.3s;

  &:active {
    transform: scale(0.8);
  }
`

const StyledShareButton = styled(ReferralShareButton)`
  padding: 4px;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`
